import React, { FC, ChangeEvent } from 'react'
import { Avatar } from 'transfix-ui/components/Avatar'
import { PhoneInput } from 'transfix-ui/components/PhoneInput'
import { IUser, ISearchedUser, USER_TYPES } from 'apps/user/types/user'
import { TextInput } from 'components/atoms'

interface IExistingUserFormProps {
  data: Partial<IUser>
  errors: { [key: string]: string }
  existingUser: ISearchedUser
  inputChangeHandler: (e: ChangeEvent<HTMLInputElement>) => void
  showCellPhone: boolean
  showEmail: boolean
  showPhone: boolean
  toggleForm: () => void
  userType: USER_TYPES
}

export const ExistingUserForm: FC<IExistingUserFormProps> = ({
  data,
  errors,
  existingUser,
  inputChangeHandler,
  showCellPhone,
  showEmail,
  showPhone,
  toggleForm,
  userType,
}) => (
  <>
    <p className='b mb4'>
      A user with this {showEmail ? 'email' : 'cell phone number'} already exists
    </p>
    <div className='card transparent bg-secondary'>
      {/* Avatar Header and Details */}
      <div className='mb4 flex items-center'>
        <div className='mr4'>
          <Avatar name={existingUser.name} tooltipName={existingUser.name} size='medium' />
        </div>
        <div>
          <p>{existingUser.name}</p>
          <p className='b pa0'>
            <a
              target='_blank'
              rel='noreferrer noopener'
              href={`${__ROOT__}/users/${existingUser.id}`}>
              View user profile
            </a>
          </p>
        </div>
      </div>

      {/* Email / Cell Phone Preview */}
      {showEmail && (
        <div className='mt1 mb4'>
          <p className='text-caption required'>Email</p>
          <p>{existingUser?.email}</p>
          <p
            className='btn-text-link tl'
            tabIndex={0}
            role='button'
            onKeyDown={toggleForm}
            onClick={toggleForm}>
            <strong>Choose a different email</strong>
          </p>
        </div>
      )}
      {showPhone && (
        <div className='mt1 mb4'>
          <p className='text-caption required'>Cell phone</p>
          <p>{existingUser?.cellPhone}</p>
          <p
            className='btn-text-link tl'
            tabIndex={0}
            role='button'
            onKeyDown={toggleForm}
            onClick={toggleForm}>
            <strong>Choose a different cell phone number</strong>
          </p>
        </div>
      )}

      {/* Contact Form */}
      <div className='row ma0 mb4'>
        <TextInput
          large
          required
          label='First name'
          placeholder='Johnny'
          value={data?.firstName}
          name='firstName'
          onChange={inputChangeHandler}
          validationMessage={errors?.firstName}
          invalid={!!errors?.firstName}
        />
      </div>
      <div className='row ma0 mb4'>
        <TextInput
          large
          required
          label='Last name'
          placeholder='Appleseed'
          value={data?.lastName}
          name='lastName'
          onChange={inputChangeHandler}
          validationMessage={errors?.lastName}
          invalid={!!errors?.lastName}
        />
      </div>
      {showEmail && (
        <div className='row pa0 mb4'>
          <div className='col-xs-8'>
            <PhoneInput
              large
              required
              label='Phone'
              value={data?.phone}
              name='phone'
              onChange={inputChangeHandler}
              validationMessage={errors?.phone}
              invalid={!!errors?.phone}
            />
          </div>
          <div className='col-xs-4'>
            <TextInput
              large
              label='Ext'
              placeholder='000'
              value={data?.phoneExt}
              inputMode='numeric'
              maxLength={3}
              validationMessage={errors?.phoneExt}
              invalid={!!errors?.phoneExt}
              name='phoneExt'
              onChange={inputChangeHandler}
            />
          </div>
        </div>
      )}
      {showCellPhone && (
        <div className='row'>
          <div className='mb4 col-xs-7'>
            <PhoneInput
              large
              required={userType !== 'dispatcher'}
              value={data?.cellPhone}
              label='Cell phone'
              name='cellPhone'
              onChange={inputChangeHandler}
              validationMessage={errors?.cellPhone}
              invalid={!!errors?.cellPhone}
            />
          </div>
        </div>
      )}
    </div>
    {userType !== USER_TYPES.admin && (
      <p className='b mt4 mb0'>
        Add this user to another {userType !== USER_TYPES.shipper ? 'carrier' : 'shipper'}?
      </p>
    )}
  </>
)
